<template>
    <CreateSavedSearchModal
        v-if="selfActiveOrganization && filtering?.current"
        :is-open="isOpen"
        :on-cancel="() => setIsOpen(false)"
        :on-save="onSavedSearchCreated"
        :on-close="() => setIsOpen(false)"
        :filtering="filtering"
        :platform="platformId"
        :source="source"
        :paging="paging"
        :result-count="futureCount"
        :count-limit="countLimit"
        :can-save="canSaveSearch"
        save-disabled-reason="Results are still loading. Try saving again once the count is available."
    />

    <DiscoveryHistory
        ref="discoveryHistory"
        :is-open="state.isSearchHistoryOpen"
        :platform-id="platformId"
        @update:is-open="state.isSearchHistoryOpen = $event"
        @run-search="(args) => onRunSearch(args)"
    />

    <SidebarLayout>
        <template #sidebar>
            <div class="flex flex-col h-full lg:h-[calc(100vh_-_var(--legacy-banner-height,0px)_-_128px)]">
                <div
                    ref="scrollContainer"
                    class="scrollbar-default flex-grow overflow-y-auto overflow-x-hidden"
                    :class="{ 'pr-1.5': hasScrollbar }"
                >
                    <div class="mb-2 flex text-left text-sm font-medium leading-5 text-slate-500">
                        <Tooltip class="my-auto mr-2 box-content">
                            <template #trigger>
                                <AppIcon
                                    class="h-3.5 w-3.5 text-slate-400 transition duration-200 hover:text-slate-500"
                                    name="circle-info"
                                />
                            </template>
                            <template #default>
                                <div class="font-bold">
                                    <h5 class="text-lg">Platform</h5>
                                    <div class="space-y-2">
                                        <p class="text-xs text-slate-300">
                                            Choose the platform you want to search creators on.
                                        </p>
                                        <p class="mt-2 text-xs text-slate-300">
                                            Streamforge displays creators with at least 1,000 followers or subscribers who have posted in the last 90 days.
                                        </p>
                                    </div>
                                </div>
                            </template>
                        </Tooltip>
                        <div>Platform</div>
                    </div>
                    <DropdownPlatform
                        :initial-selected-source-id="platformId"
                        :platforms="selectablePlatforms"
                        :counts="counts"
                        @change="onPlatformChange"
                    />
                    <div class="mt-4 mb-6">
                        <div class="mb-2 flex text-left text-sm font-medium leading-5 text-slate-500">
                            <Tooltip class="my-auto mr-2 box-content">
                                <template #trigger>
                                    <AppIcon
                                        class="h-3.5 w-3.5 text-slate-400 transition duration-200 hover:text-slate-500"
                                        name="circle-info"
                                    />
                                </template>
                                <template #default>
                                    <div class="font-bold">
                                        <h5 class="text-lg">Search in</h5>
                                        <div class="space-y-2">
                                            <p class="text-xs text-slate-300">
                                                Search across all active creators in Streamforge or focus on creators from one of your lists.
                                            </p>
                                        </div>
                                    </div>
                                </template>
                            </Tooltip>
                            <div>Search in</div>
                        </div>
                        <DropdownSource
                            is-discovery
                            :platform-id="platformId"
                            :lists-by-platform-id="listsByPlatformId"
                            :campaigns-by-platform-id="campaignsByPlatformId"
                            :include-platform="false"
                            :use-enhanced-selection="true"
                            :allow-multiple-lists="true"
                            :allow-multiple-campaigns="true"
                            :initial-selected-source="source"
                            :counts="counts"
                            @change="onSourceChange"
                        />
                        <div v-if="filtering?.current" class="mt-4">
                            <div class="mb-2 flex text-left text-sm font-medium leading-5 text-slate-500">
                                <Tooltip class="my-auto mr-2 box-content">
                                    <template #trigger>
                                        <AppIcon
                                            class="h-3.5 w-3.5 text-slate-400 transition duration-200 hover:text-slate-500"
                                            name="circle-info"
                                        />
                                    </template>
                                    <template #default>
                                        <div class="font-bold">
                                            <h5 class="text-lg">Exclude creators in list</h5>
                                            <p class="text-xs text-slate-300">
                                                Hide creators from the selected list in your search results.
                                            </p>
                                        </div>
                                    </template>
                                </Tooltip>
                                <div>Exclude creators in list</div>
                            </div>
                            <DropdownSource
                                is-discovery
                                :platform-id="platformId"
                                :lists-by-platform-id="listsByPlatformId"
                                :include-platform="false"
                                :include-campaigns="false"
                                :use-enhanced-selection="true"
                                :allow-multiple-lists="true"
                                :show-no-source-selected="true"
                                no-source-selected-message="No creators excluded"
                                :initial-selected-source="getExcludedListSource()"
                                @change="onExcludedListChange"
                            />
                        </div>
                    </div>
                    <div
                        class="text-md relative mb-2 flex border-b border-slate-300 pb-2 text-left font-medium leading-5 text-slate-500"
                    >
                        <div class="flex">
                            <AppIcon
                                name="filters"
                                class="my-auto h-5 w-5"
                            />
                            <span class="ml-2">Filters</span>
                        </div>
                        <div class="-mx-2 my-auto ml-auto text-xs font-semibold">
                            <span class="border-r border-slate-400 px-2">
                                <button
                                    type="button"
                                    class="cursor-pointer hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                                    :disabled="!canSaveSearch"
                                    :title="canSaveSearch ? 'Save current filters' : 'Results are still loading'"
                                    @click="setIsOpen(true)"
                                >
                                    Save
                                </button>
                            </span>
                            <span v-if="selfActiveOrganization" class="border-r border-slate-400 px-2">
                                <span class="cursor-pointer hover:underline" @click="state.isSearchHistoryOpen = true">History</span>
                            </span>
                            <span class="cursor-pointer px-2 hover:underline" @click="onReset">Reset</span>
                        </div>
                    </div>
                    <TargetingFilters
                        v-if="filtering?.current"
                        :platform-id="platformId"
                        :filtering="filtering"
                        :lists="listsByPlatformId"
                        @filter-change-single="onSingleFilterChange"
                    />
                </div>
                <div class="mt-4 mb-6 lg:mb-0 flex w-full flex-col">
                    <div class="w-full h-6 text-center bg-white border border-slate-200 rounded text-xs mb-1 flex">
                        <div v-if="state.counts.isLoading === false && futureCount !== null" class="m-auto">
                            <template v-if="futureCount >= this.countLimit">
                                Over {{ $numeral(futureCount).format('0,0') }} creators with these filters
                            </template>
                            <template v-else>
                                {{ $numeral(futureCount).format('0,0') }} creators with these filters
                            </template>
                        </div>
                        <div v-else class="m-auto">Loading</div>
                    </div>
                    <Tooltip :show-tooltip="state.isFiltersDirty !== true">
                        <template #trigger>
                            <button
                                class="btn pointer-events-auto mt-auto w-full bg-indigo-500 text-white shadow-none hover:bg-indigo-600 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-50 disabled:text-slate-500"
                                :class="{
                                    'pointer-events-none !cursor-default': state.creators.isBusy === true,
                                }"
                                :disabled="state.isFiltersDirty !== true"
                                @click="onApplyFilters"
                            >
                                <AppIcon
                                    v-if="state.creators.isBusy"
                                    name="spinner-third"
                                    class="mr-2 h-4 w-4 animate-spin"
                                />
                                <span>Apply Filters</span>
                                <span v-if="filtersAppliedCount" class="ml-0.5 tracking-wider">({{ filtersAppliedCount }})</span>
                            </button>
                        </template>
                        <template v-if="state.isFiltersDirty !== true" #default>
                            <div class="whitespace-nowrap text-xs">Change around some filters to submit a search!</div>
                        </template>
                    </Tooltip>
                </div>
            </div>
        </template>

        <template #main>
            <div ref="discoverTable" class="h-full lg:h-[calc(100vh_-_var(--legacy-banner-height,0px)_-_128px)]">
                <DiscoverTable
                    :paging="paging"
                    :creators="creators"
                    :is-loading="state.creators.isLoading"
                    :is-busy="state.creators.isBusy"
                    :columns="tableColumns"
                    :enabled-columns="state.columns.enabled"
                    :platform-id="platformId"
                    :is-selectable="true"
                    :show-pre-generation-slot="state.creators.showPreGeneration"
                    :count-limit="this.countLimit"
                    @add-to-list="addListCreators"
                    @add-to-campaign="addCreatorsToCampaign"
                    @paging-change="onPagingChange"
                    @export-creators="onExportCreators"
                    @enabled-column-change="onEnabledColumnChange"
                    @filter-change="onFilterChange"
                >
                    <template #header-toolbar>
                        <ColumnDisplayManager
                            :available-columns="tableColumns"
                            :groups="availableColumnGroups"
                            :model-value="state.columns.enabled"
                            :default-columns="defaultDiscoveryColumns"
                            @update:model-value="(newEnabledColumns) => onEnabledColumnChange(newEnabledColumns)"
                        />
                    </template>
                    <template #creator="{ field }">
                        <div class="my-1">
                            <DiscoveryProfileUnlock
                                :platform-id="platformId"
                                :platform-user-id="field.id"
                                :modal="'creator'"
                                :logo="field.logo"
                                :name="field.name"
                                :display-name="field.display_name"
                            />
                        </div>
                    </template>
                    <template #affinity="{ item, field, column }">
                        <AffinityPlate
                            :affinity-percent="field"
                            :class="{
                                'opacity-25': column.is_disabled,
                            }"
                            :metadata="metadata"
                            :affinity="item.affinity"
                            :discover-entry="item"
                        />
                    </template>
                    <template #code="{ field, column }">
                        <CodePlate
                            v-if="codes?.[column?.row?.code_type]?.[field]"
                            :code="codes?.[column?.row?.code_type]?.[field]"
                        />
                    </template>
                    <template #platforms="{ field }">
                        <div class="flex space-x-2">
                            <a
                                v-for="platform in field.platformLinks"
                                :key="platform.platformId"
                                :href="platform.link"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="flex items-center"
                            >
                                <AppIcon
                                    :name="platform.platformId === 'twitter' ? 'x-twitter' : platform.platformId"
                                    class="h-4 w-4"
                                    :class="platform.platformId === 'twitter' ? 'text-x' : `text-${platform.platformId}`"
                                />
                            </a>
                        </div>
                    </template>
                    <template #pre-generation>
                        <div class="w-full m-auto p-4 text-center">
                            <p class="text-sm">Please select some filters to discover creators.</p>
                        </div>
                    </template>
                </DiscoverTable>
            </div>
        </template>
    </SidebarLayout>
</template>

<script>
import _ from 'lodash'
import numeral from 'numeral'
import { mapGetters } from 'vuex'
import { json2csvAsync } from 'json-2-csv'
import { SidebarLayout } from '@/components/ui/layouts'
import DropdownPlatform from '@/components/dropdowns/DropdownDiscoverPlatform.vue'
import DropdownSource from '@/components/dropdowns/DropdownDiscoverSource.vue'
import Tooltip from '@/components/Tooltip.vue'
import AffinityPlate from '@/components/plates/AffinityPlate.vue'
import CodePlate from '@/components/plates/CodePlate.vue'
import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import TargetingFilters from '@/partials/options/TargetingFilters.vue'
import ColumnDisplayManager from '@/partials/options/ColumnDisplayManager.vue'
import targetGroupTools from '@/utils/searching/targetgroups'
import targetGroupAPI from '@/utils/api/api.targeting'
import DiscoveryHistory from '@/partials/modals/DiscoveryHistory/DiscoveryHistory.vue'
import CreateSavedSearchModal from '@/partials/modals/CreateSavedSearchModal/CreateSavedSearchModal.vue'
import DiscoveryProfileUnlock from '@/components/unlocks/DiscoveryProfileUnlock.vue'
import { trackEvent, AnalyticalEvents } from '@/utils/analytics'
import { BREAKPOINTS } from '@/constants'

const platforms = ['instagram', 'tiktok', 'youtube', 'twitch', 'twitter']

export default {
    name: 'DiscoverPage',

    metaInfo: {
        title: 'Discovery',
    },

    components: {
        SidebarLayout,
        DropdownPlatform,
        DropdownSource,
        Tooltip,
        AffinityPlate,
        CodePlate,
        DiscoverTable,
        TargetingFilters,
        ColumnDisplayManager,
        DiscoveryHistory,
        CreateSavedSearchModal,
        DiscoveryProfileUnlock,
    },

    data() {
        return {
            isOpen: false,
            creators: [],
            totalRecords: 0,

            counts: null,
            countLimit: 10000,
            futureCount: null,


            columnGroups: [
                { id: 'basic', label: 'Basic', platforms : ['instagram', 'tiktok', 'youtube', 'twitch', 'twitter'] },
                { id: 'metrics', label: 'Metrics', platforms : ['instagram', 'tiktok', 'youtube', 'twitch', 'twitter'] },
                { id: 'audience', label: 'Audience', platforms : ['instagram', 'tiktok', 'youtube', 'twitch', 'twitter'] },
                { id: 'games', label: 'Games', platforms : ['youtube', 'twitch'] },
            ],

            supercampaigns: [],
            campaigns: [],
            campaignsByPlatformId: {
                twitch: [],
                youtube: [],
                tiktok: [],
            },
            lists: [],
            listsByPlatformId: {
                twitch: [],
                youtube: [],
                tiktok: [],
            },
            targetGroups: {},

            codes: {},

            selectedTargetingGroup: '',
            platformGroups: [],

            platformId: 'instagram',

            source: {
                type: 'platform',
                input: 'instagram',
            },

            nameSearch: null,

            filtering: {
                current: null,
                default: null,
                options: null,
            },

            filteringByPlatform: _.reduce(platforms, (result, platformId) => {
                result[platformId] = targetGroupTools.createDiscoveryFilters(platformId)
                return result
            }, {}),

            metadata: {
                games: [],
                languages: [],
                demographics: {},
            },

            paging: {
                perPage: 15,
                currentPage: 1,
                totalRecords: 0,
                totalPages: 1,
                sort: [
                    {
                        field: 'avg_viewers',
                        direction: 'desc',
                        source: 'default',
                    },
                ],
            },

            state: {
                creators: {
                    isLoading: false,
                    isBusy: false,
                    showPreGeneration: true,
                },
                columns: {
                    enabled: [],
                },
                isFiltersDirty: true,
                counts: {
                    isLoading: false,
                },

                isSearchHistoryOpen: false,
            },

            isSmallScreen: window.innerWidth < BREAKPOINTS.md,
            hasScrollbar: false,
        }
    },

    computed: {
        ...mapGetters([
            'isSfStaff',
            'getLanguageFromCode',
            'getCountryFromCode',
            'selfActiveOrganization',
            'checkPermission',
            'preferences',
            'topCreator',
        ]),

        query() {
            return this.$route.query
        },

        targetingGroups() {
            return [
                {
                    name: 'Discovery Search',
                    filters: _.assign(_.cloneDeep(this.filtering.current), {
                        platform: this.platformId,
                    }),
                },
            ]
        },

        filtersAppliedCount() {
            if (!this.filtering || !this.filtering.current || !this.filtering.default) {
                return 0
            }

            return Object.keys(this.filtering.default).reduce((count, key) => {
                if (['content_type', 'day_range'].includes(key)) return 0
                return count + (!_.isEqual(this.filtering.current[key], this.filtering.default[key]) ? 1 : 0)
            }, 0)
        },

        has0FiltersApplied() {
            return _.isEqual(this.filtering.current, this.filtering.default)
        },

        canSaveSearch() {
            return Boolean(
                this.selfActiveOrganization &&
                this.filtering?.current &&
                this.state.counts.isLoading === false &&
                this.futureCount !== null
            )
        },

        tableColumns() {
            return this.getAvailableColumns()
        },

        defaultDiscoveryColumns() {
            const averageViewsColumn = this.platformId === 'twitch' ? 'avg_viewers' : 'avg_views'
            const defaultColumns = [
                'creator',
                'country',
                'language',
                'followers',
                averageViewsColumn,
                'avg_engagement_rate',
            ]
            const availableColumnIds = new Set(this.tableColumns.map((column) => column.id))

            return defaultColumns.filter((columnId) => availableColumnIds.has(columnId))
        },

        selectablePlatforms() {
            const availablePlatforms = ['instagram', 'tiktok', 'youtube', 'twitch', 'twitter']

            return availablePlatforms
        },

        availableColumnGroups() {
            return _.filter(this.columnGroups, (group) => {
                return group?.platforms?.includes(this.platformId) && _.some(this.tableColumns, { group: group.id })
            })
        },

        orgDefaultPlatformId() {
            return this.$store.getters.selfDefaultPlatformId
        },
    },

    watch: {
        // Resets the currentPage index when it's higher than totalPages
        'paging.totalPages': {
            handler() {
                if (this.paging.totalPages !== null && this.paging.currentPage > this.paging.totalPages) {
                    this.paging.currentPage = this.paging.totalPages
                    this.findCreators({
                        isExport: false,
                        isInitial: false,
                    })
                }
            },
        },

        'filtering.current.day_range': function (newValue, oldValue) {
            if (!oldValue || newValue !== oldValue) {
                this.setEnabledColumnDefaults()
            }
        },

        'filtering.current.content_type': function (newValue, oldValue) {
            if (!oldValue || newValue !== oldValue) {
                this.setEnabledColumnDefaults()
                this.resetSorting()
                this.checkSortingOverride()
            }
        },

        platformId: {
            handler(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.setEnabledColumnDefaults()
                }
            },
            deep: false,
            immediate: true,
        },

        topCreator: {
            handler(newValue) {
                if (newValue && !newValue.targetingGroups && this.targetingGroups) {
                    this.$store.commit('modal:setData', {
                        modal_id: 'creator',
                        data: {
                            is_open: true,
                            platform_id: newValue.platformId,
                            platform_user_id: newValue.platformUserId,
                            targetingGroups: this.targetingGroups,
                        },
                    })
                }
            },
            immediate: true,
        },

        targetingGroups: {
            handler(newValue) {
                if (this.topCreator) {
                    this.$store.commit('modal:setData', {
                        modal_id: 'creator',
                        data: {
                            is_open: true,
                            platform_id: this.topCreator.platformId,
                            platform_user_id: this.topCreator.platformUserId,
                            targetingGroups: newValue,
                        },
                    })
                }
            },
            deep: true,
        },
    },

    mounted() {
        this.onResize()
        window.addEventListener('resize', this.onResize)

        this.checkScrollbar()

        this.resizeObserver = new ResizeObserver(() => {
            this.checkScrollbar()
        })

        this.resizeObserver.observe(this.$refs.scrollContainer)
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.onResize)
        this.resizeObserver.disconnect()
    },

    async created() {
        if (this.preferences?.tables?.discovery?.rowsPerPage) {
            this.paging.perPage = this.preferences.tables.discovery.rowsPerPage
        }

        if (this.orgDefaultPlatformId) {
            this.platformId = this.orgDefaultPlatformId
            this.source.input = this.orgDefaultPlatformId
        }

        this.getCounts()
        this.getLists()
        this.getSupercampaigns()
        // this.getTargetGroups()

        // Are we loading the page trying to access an previous/someone else's discovery request?
        if (this.$route.query?.id) {

            // Fetch the search from the backend
            await this.getPreviousSearch(this.$route.query?.id)
        }

        this.$nextTick(() => {
            this.onPlatformChange({
                type: 'platform',
                value: {
                    id: this.platformId,
                },
            })
        })
    },

    methods: {
        onRunSearch(search) {
            const request = this.getSearchRequest(search)

            if (request) {
                return this.applyDiscoveryRequest(request, search)
            }

            if (search?.request_hash) {
                this.$router.replace({
                    query: {
                        id: search.request_hash
                    },
                })

                return this.getPreviousSearch(search.request_hash)
            }

            return false
        },

        getSearchRequest(search) {
            if (search?.request?.params && search?.request?.body) {
                return search.request
            }

            if (search?.request?.request?.params && search?.request?.request?.body) {
                return search.request.request
            }

            return null
        },

        getPreviousSearch(requestHash) {
            this.state.creators.showPreGeneration = false
            this.state.creators.isLoading = true
            return this.$axios.get(`/discovery/history/${requestHash}`)
                .then(({ data }) => {
                    const previousRequest = data?.payload?.discoveryRequest?.request

                    if (!previousRequest) {
                        this.state.creators.isLoading = false
                        return false
                    }

                    return this.applyDiscoveryRequest(previousRequest)
                })
                .catch((error) => {
                    console.error('Error loading old discovery request', error)
                })
        },

        applyDiscoveryRequest(previousRequest, search = null) {
            this.state.creators.showPreGeneration = false
            this.state.creators.isLoading = true
            const requestBody = previousRequest?.body || {}
            this.platformId = previousRequest?.params?.platform_id || this.platformId

            const query = _.cloneDeep(requestBody.query || {})
            const savedSource = search?.source || previousRequest.source || requestBody.source

            this.source.type = 'platform'
            this.source.input = null
            this.source.name = null

            if (_.size(query.list_id) > 0 && _.size(query.campaign_id) > 0) {
                this.source.type = 'mixed'
                this.source.input = {
                    list_id: query.list_id,
                    campaign_id: query.campaign_id,
                }
                delete query.list_id
                delete query.campaign_id
            } else if (_.size(query.list_id) > 0) {
                this.source.type = 'list'
                this.source.input = query.list_id
                delete query.list_id
            } else if (_.size(query.campaign_id) > 0) {
                this.source.type = 'campaign'
                this.source.input = query.campaign_id
                delete query.campaign_id
            } else if (savedSource?.type && savedSource.type !== 'platform' && savedSource.input) {
                this.source.type = savedSource.type
                this.source.input = savedSource.input
            }

            if (savedSource?.name && this.source.type === savedSource.type) {
                this.source.name = savedSource.name
            }

            if (this.filteringByPlatform[this.platformId]) {
                this.filtering = this.filteringByPlatform[this.platformId]

                this.filtering.current = _.assign(_.cloneDeep(this.filtering.default), {
                    ...requestBody.collection,
                    ...query,
                })

                this.resetSorting()

                // Reset paging settings
                this.clear()

                // this.setTargetGroups()
                this.invalidateCreatorCount()
                this.getCreatorCounts()

                this.findCreators({
                    isExport: false,
                    isInitial: true,
                })

                return true
            }

            this.state.creators.isLoading = false
            return false
        },

        getCounts() {
            this.$axios.get('/discovery/counts').then(({ data }) => {
                this.counts = data.payload.counts
            })
        },

        setEnabledColumnDefaults() {
            const savedColumns = this.preferences?.tables?.discovery?.[this.platformId]?.columns

            if (savedColumns) {
                this.state.columns.enabled = this.filterEnabledColumns(savedColumns)
            } else {
                this.state.columns.enabled = [...this.defaultDiscoveryColumns]
            }
        },

        filterEnabledColumns(columnIds) {
            const availableColumnIds = new Set(this.tableColumns.map((column) => column.id))

            return _.filter(columnIds, (columnId) => availableColumnIds.has(columnId))
        },

        getAvailableColumns() {
            const fields = []

            const metricWindow = _.get(this.filtering.current, 'day_range')
            const contentType = _.get(this.filtering.current, 'content_type')

            let metricWindowName = ''
            if (metricWindow === 1095) {
                metricWindowName = '3 year'
            } else if (!metricWindow) {
                metricWindowName = '365 day' // Probably hasn't loaded yet
            } else {
                metricWindowName = `${metricWindow} day`
            }

            const metricWindowTooltipName = metricWindow === 1095
                ? 'Last 3 years'
                : `Last ${metricWindow || 365} days`

            const metricTooltip = (title, text) => ({
                title,
                sub_title: metricWindowTooltipName,
                content: [
                    {
                        text,
                    },
                ],
            })

            const followerTooltip = {
                title: 'Followers',
                content: [
                    {
                        text: 'Current follower count.',
                    },
                ],
            }

            const subscriberTooltip = {
                title: 'Subscribers',
                content: [
                    {
                        text: 'Current subscriber count.',
                    },
                ],
            }

            const engagementRateTooltip = metricTooltip(
                'Engagement rate',
                'Average engagement rate across the creator’s recent content.',
            )

            fields.push({
                id: 'creator',
                key: 'superview',
                header: {
                    label: 'Creator',
                    is_sortable: false,
                    class: 'w-64 min-w-64',
                },
                row: {
                    class: '',
                    slot: 'creator',
                    slotTransformer: (item) => ({
                        // eslint-disable-next-line no-underscore-dangle
                        id: item?.superview?._id,
                        name: item.superview.normalized.name,
                        display_name: item.superview.normalized.display_name,
                        logo: item.superview.normalized.avatar_url,
                    }),
                },
                is_sticky: true,
                is_exportable: true,
                exportTransformer: (item) => ({
                    // eslint-disable-next-line no-underscore-dangle
                    ID: item.superview._id,
                    Handle: item?.superview?.normalized?.name,
                    'Display Name': item?.superview?.normalized?.display_name,
                }),
                group: 'basic',
            })

            // Add platforms column
            fields.push({
                id: 'platforms',
                key: 'superview.normalized.socials',
                header: {
                    label: 'Socials',
                    is_sortable: false,
                    class: 'w-32',
                },
                row: {
                    class: '',
                    slot: 'platforms',
                    slotTransformer: (item) => {
                        const socials = item?.superview?.normalized?.socials || []

                        const platformLinksArray = Object.entries(socials)
                            .filter(([key]) => platforms.includes(key))
                            .map(([platformId, social]) => ({
                                platformId,
                                link: social.link,
                            }))

                        // Sort the array to prioritize the one matching this.platformId first
                        // Then follow the order from `platforms`
                        const platformLinks = platformLinksArray.sort((a, b) => {
                            // Prioritize the one matching this.platformId
                            if (a.platformId === this.platformId) return -1
                            if (b.platformId === this.platformId) return 1
                            // Otherwise follow the order from `platforms`
                            return platforms.indexOf(a.platformId) - platforms.indexOf(b.platformId)
                        })

                        return { platformLinks }
                    },
                },
                is_exportable: false,
                group: 'basic',
            })

            if (this.platformId === 'twitch') {
                fields.push({
                    id: 'avg_viewers',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.viewers.avg`,
                    header: {
                        label: 'AVG Viewers',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: {
                            title: 'Average Concurrent Viewers',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The average number of viewers you would expect to see in this creator\'s stream at any point within the aggregation window.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_viewers',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.viewers.peak.value`,
                    header: {
                        label: 'Peak Viewers',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Peak Concurrent Viewers',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The largest amount of concurrent viewers that were watching the creator\'s stream at once during the aggregation window.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_engagement_rate',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.engagement_rate.avg`,
                    header: {
                        label: 'Engagement rate',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: engagementRateTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => numeral(field).format('0.0%'),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_airtime',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.live_seconds.avg`,
                    header: {
                        label: 'Avg Airtime',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Average Airtime',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The average duration of this creator\'s streams.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.humanizeDurationShort(Math.round(field) * 1000),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_airtime',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.live_seconds.sum`,
                    header: {
                        label: 'Sum Airtime',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Sum Airtime',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The total duration of this creator\'s streams during the aggregation window.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.humanizeDurationShort(Math.round(field) * 1000),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_airtime',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.live_seconds.peak.value`,
                    header: {
                        label: 'Peak Airtime',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Peak Airtime',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The longest stream this creator has performed during the aggregation window.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.humanizeDurationShort(Math.round(field) * 1000),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_impressions',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.impressions.avg`,
                    header: {
                        label: 'Avg Impressions',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Average Impressions per Stream',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The average amount of people that see the creator\'s stream during the aggregation window. A single person can generate multiple impressions on a stream.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_impressions',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.impressions.sum`,
                    header: {
                        label: 'Sum Impressions',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Sum Impressions per Stream',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The total amount of times that a person saw the creator\'s streams during the aggregation window. A single person can generate multiple impressions on a stream.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_minutes_watched',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.minutes_watched.avg`,
                    header: {
                        label: 'Avg Minutes Watched',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Average Minutes Watched',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The average amount of minutes that were watched by the creator\'s audience during live streams in the aggregation window.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_minutes_watched',
                    key: `superview.stream.day_${String(metricWindow)}.aggregation.minutes_watched.sum`,
                    header: {
                        label: 'Sum Minutes Watched',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: {
                            title: 'Sum Minutes Watched',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The total amount of minutes that were watched by the creator\'s audience during live streams in the aggregation window.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'games_count',
                    key: `superview.stream.day_${String(metricWindow)}.accumulation.games.count`,
                    header: {
                        label: 'Games covered',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total games covered', 'The total number of different games the creator has covered within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'games',
                })
                fields.push({
                    id: 'sum_content_items',
                    key: `superview.stream.day_${String(metricWindow)}.accumulation.streams.count`,
                    header: {
                        label: 'Total Streams',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: {
                            title: 'Total Streams',
                            sub_title: `${metricWindowName} aggregation`,
                            content: [
                                {
                                    text: 'The total number of streams during the aggregation window.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'basic',
                })
                fields.push({
                    id: 'followers',
                    key: 'superview.channel.followers',
                    header: {
                        label: 'Followers',
                        is_sortable: true,
                        tooltip: followerTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
            }

            if (this.platformId === 'youtube') {
                if (contentType === 'stream') {
                    fields.push({
                        id: 'avg_viewers',
                        key: `superview.stream.day_${String(metricWindow)}.aggregation.viewers.avg`,
                        header: {
                            label: 'AVG Viewers',
                            sub_label: `${metricWindowName}`,
                            is_sortable: true,
                            tooltip: {
                                title: 'Average Concurrent Viewers',
                                sub_title: `${metricWindowName} aggregation`,
                                content: [
                                    {
                                        text: 'The average number of viewers you would expect to see in this creator\'s stream at any point within the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                        },
                        is_exportable: true,
                        group: 'metrics',
                    })
                }

                fields.push({
                    id: 'avg_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.avg`,
                    header: {
                        label: 'Avg. views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average views', 'Average views across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })

                fields.push({
                    id: 'peak_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.peak.value`,
                    header: {
                        label: 'Peak views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak views', 'Highest view count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.sum`,
                    header: {
                        label: 'Total views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total views', 'Total views across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_engagement_rate',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.engagement_rate.avg`,
                    header: {
                        label: 'Engagement rate',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: engagementRateTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => numeral(field).format('0.0%'),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.avg`,
                    header: {
                        label: 'Avg. comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average comments', 'Average comments across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.peak.value`,
                    header: {
                        label: 'Peak comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak comments', 'Highest comment count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.sum`,
                    header: {
                        label: 'Total comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total comments', 'Total comments across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_content_items',
                    key: `superview.${contentType}.day_${String(metricWindow)}.accumulation.videos.count`,
                    header: {
                        label: 'Total posts',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total posts', 'The total number of videos during the aggregation window.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.avg`,
                    header: {
                        label: 'Avg. likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average likes', 'Average likes across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.peak.value`,
                    header: {
                        label: 'Peak likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak likes', 'Highest like count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.sum`,
                    header: {
                        label: 'Total likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Total likes', 'Total likes across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'followers',
                    key: 'superview.channel.statistics.subscriberCount',
                    header: {
                        label: 'Subscribers',
                        is_sortable: true,
                        tooltip: subscriberTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'games_count',
                    key: `superview.${contentType}.day_${String(metricWindow)}.accumulation.games.count`,
                    header: {
                        label: 'Games covered',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total games covered', 'The total number of different games the creator has covered within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'games',
                })
            }

            if (this.platformId === 'tiktok') {
                fields.push({
                    id: 'avg_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.avg`,
                    header: {
                        label: 'Avg. views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average views', 'Average views across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.peak.value`,
                    header: {
                        label: 'Peak views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak views', 'Highest view count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.sum`,
                    header: {
                        label: 'Total views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total views', 'Total views across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_engagement_rate',
                    key: 'discovery.avg_engagement_rate',
                    header: {
                        label: 'Engagement rate',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: engagementRateTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => numeral(field).format('0.0%'),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.avg`,
                    header: {
                        label: 'Avg. comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average comments', 'Average comments across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.peak.value`,
                    header: {
                        label: 'Peak comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak comments', 'Highest comment count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.sum`,
                    header: {
                        label: 'Total comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Total comments', 'Total comments across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_content_items',
                    key: `superview.video.day_${String(metricWindow)}.accumulation.videos.count`,
                    header: {
                        label: 'Total posts',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total posts', 'The total number of videos during the aggregation window.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.avg`,
                    header: {
                        label: 'Avg. likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average likes', 'Average likes across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.peak.value`,
                    header: {
                        label: 'Peak likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak likes', 'Highest like count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.sum`,
                    header: {
                        label: 'Total likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Total likes', 'Total likes across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_shares',
                    key: `superview.video.day_${String(metricWindow)}.aggregation.shares.avg`,
                    header: {
                        label: 'Avg. shares',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average shares', 'Average shares across the creator\'s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'followers',
                    key: 'superview.channel.statistics.followers',
                    header: {
                        label: 'Followers',
                        is_sortable: true,
                        tooltip: followerTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
            }

            if (this.platformId === 'twitter') {
                fields.push({
                    id: 'avg_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.avg`,
                    header: {
                        label: 'Avg. views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average views', 'Average views across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.peak.value`,
                    header: {
                        label: 'Peak views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Peak views', 'Highest view count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.sum`,
                    header: {
                        label: 'Total views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total views', 'Total views across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_engagement_rate',
                    key: 'discovery.avg_engagement_rate',
                    header: {
                        label: 'Engagement rate',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: engagementRateTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => numeral(field).format('0.0%'),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.replies.avg`,
                    header: {
                        label: 'Avg. comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Average comments', 'Average comments across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.replies.peak.value`,
                    header: {
                        label: 'Peak comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak comments', 'Highest comment count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.replies.sum`,
                    header: {
                        label: 'Total comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Total comments', 'Total comments across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_content_items',
                    key: `superview.post.day_${String(metricWindow)}.accumulation.posts.count`,
                    header: {
                        label: 'Total posts',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total posts', 'The total number of videos during the aggregation window.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.avg`,
                    header: {
                        label: 'Avg. likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average likes', 'Average likes across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.peak.value`,
                    header: {
                        label: 'Peak likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak likes', 'Highest like count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.sum`,
                    header: {
                        label: 'Total likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Total likes', 'Total likes across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_shares',
                    key: `superview.post.day_${String(metricWindow)}.aggregation.retweets.avg`,
                    header: {
                        label: 'Avg. shares',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average shares', 'Average shares across the creator\'s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_quotes',
                    key: `superview.post.day_${String(metricWindow)}.aggregation.quotes.avg`,
                    header: {
                        label: 'Avg. quotes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average quotes', 'Average quotes across the creator\'s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'followers',
                    key: 'superview.channel.followers_count',
                    header: {
                        label: 'Followers',
                        is_sortable: true,
                        tooltip: followerTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
            }

            if (this.platformId === 'instagram') {
                fields.push({
                    id: 'avg_views',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.views.avg`,
                    header: {
                        label: 'Avg. views',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average views', 'Average views across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.avg`,
                    header: {
                        label: 'Avg. likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Average likes', 'Average likes across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.peak.value`,
                    header: {
                        label: 'Peak likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak likes', 'Highest like count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_likes',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.likes.sum`,
                    header: {
                        label: 'Total likes',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Total likes', 'Total likes across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_engagement_rate',
                    key: 'discovery.avg_engagement_rate',
                    header: {
                        label: 'Engagement rate',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: engagementRateTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => numeral(field).format('0.0%'),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'avg_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.avg`,
                    header: {
                        label: 'Avg. comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Average comments', 'Average comments across the creator’s recent content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'peak_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.peak.value`,
                    header: {
                        label: 'Peak comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Peak comments', 'Highest comment count reached by a single piece of content.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_comments',
                    key: `superview.${contentType}.day_${String(metricWindow)}.aggregation.comments.sum`,
                    header: {
                        label: 'Total comments',
                        sub_label: `${metricWindowName}`,
                        is_sortable: false,
                        tooltip: metricTooltip('Total comments', 'Total comments across the creator’s content within the selected timeframe.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'sum_content_items',
                    key: `superview.post.day_${String(metricWindow)}.accumulation.posts.count`,
                    header: {
                        label: 'Total posts',
                        sub_label: `${metricWindowName}`,
                        is_sortable: true,
                        tooltip: metricTooltip('Total posts', 'The total number of videos during the aggregation window.'),
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
                fields.push({
                    id: 'followers',
                    key: 'superview.channel.statistics.followers',
                    header: {
                        label: 'Followers',
                        is_sortable: true,
                        tooltip: followerTooltip,
                    },
                    row: {
                        class: '',
                        formatter: ({ field }) => this.$filters.formatWholeNumberWithUnit(field),
                    },
                    is_exportable: true,
                    group: 'metrics',
                })
            }
            fields.push({
                id: 'language',
                key: 'discovery.language',
                header: {
                    label: 'Language',
                    is_sortable: true,
                    tooltip: {
                        title: 'Content language',
                        content: [
                            {
                                text: 'The primary languages the creator uses in their content based on publicly available information and AI analysis.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field ? this.getLanguageFromCode(field) : '',
                },
                is_exportable: true,
                group: 'basic',
            })
            fields.push({
                id: 'country',
                key: 'discovery.country',
                header: {
                    label: 'Country',
                    is_sortable: true,
                    tooltip: {
                        title: 'Country of residence',
                        content: [
                            {
                                text: 'The creator’s country of residence based on publicly available information and AI analysis.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field ? this.getCountryFromCode(field) : '',
                },
                is_exportable: true,
                group: 'basic',
            })
            fields.push({
                id: 'region',
                key: 'discovery.region',
                header: {
                    label: 'Region',
                    is_sortable: true,
                    tooltip: {
                        title: 'Region of residence',
                        content: [
                            {
                                text: 'The creator’s region of residence based on publicly available information and AI analysis.',
                            },
                        ],
                    },
                },
                is_exportable: true,
                group: 'basic',
            })
            fields.push({
                id: 'gender',
                key: 'discovery.gender',
                header: {
                    label: 'Gender',
                    is_sortable: true,
                    tooltip: {
                        title: 'Gender',
                        content: [
                            {
                                text: 'The creator’s gender based on publicly available information and AI analysis.',
                            },
                        ],
                    },
                },
                // row: {
                //     slot: 'code',
                //     code_type: 'gender',
                // },
                row: {
                    class: '',
                    formatter: ({ field }) => {
                        if (field && field.length > 0) {
                            return field.charAt(0).toUpperCase() + field.slice(1)
                        }
                        return field
                    },
                },
                is_exportable: true,
                group: 'basic',
            })
            fields.push({
                id: 'age_range',
                key: 'discovery.age_range',
                header: {
                    label: 'Age range',
                    is_sortable: true,
                    tooltip: {
                        title: 'Age range',
                        content: [
                            {
                                text: 'The creator’s age range based on publicly available information and AI analysis.',
                            },
                        ],
                    },
                },
                // row: {
                //     slot: 'code',
                //     code_type: 'age-range',
                // },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'basic',
            })
            fields.push({
                id: 'main_industry',
                key: 'discovery.classifications.0.topic',
                header: {
                    label: 'Main niche',
                    is_sortable: true,
                    tooltip: {
                        title: 'Niche',
                        content: [
                            {
                                text: 'The main topics the creator regularly makes content about based on AI analysis of their content.',
                            },
                        ],
                    },
                },
                // row: {
                //     slot: 'code',
                //     code_type: 'industry',
                // },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'basic',
            })
            /*
             * TODO: Reinstate when Industry Category is improved.
            fields.push({
                id: 'main_industry_category',
                key: 'discovery.classifications.0.category',
                header: {
                    label: 'Main Industry Category',
                    is_sortable: false,
                    tooltip: {
                        title: 'Main Industry Category',
                        content: [
                            {
                                text: 'This is an estimate made by AI to determine the main industry category of the creator.',
                            },
                        ],
                    },
                },
                // row: {
                //     slot: 'code',
                //     code_type: 'industry_category',
                // },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'basic',
            })
            */
            fields.push({
                id: 'account_rating_remapped',
                key: 'discovery.account_rating_remapped',
                header: {
                    label: 'Content rating',
                    is_sortable: true,
                    tooltip: {
                        title: 'Content Rating',
                        content: [
                            {
                                text: 'The maturity level of the creator’s content based on AI analysis.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'basic',
            })

            if (_.size(this?.filtering?.current?.games?.values) > 0) {
                fields.push({
                    id: 'affinity',
                    key: 'affinity.game.percent',
                    header: {
                        label: 'Affinity',
                        is_sortable: true,
                        tooltip: {
                            title: 'Affinity',
                            content: [
                                {
                                    text: 'Affinity represents how well a creator and their audience match a given targeting group.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        slot: 'affinity',
                    },
                    is_exportable: true,
                    is_disabled:
                        !this.filtering?.current?.games?.values || _.size(this.filtering.current?.games?.values) === 0,
                    group: 'games',
                })
            }

            if (['twitch', 'youtube'].includes(this.platformId)) {

                fields.push({
                    id: 'game_primary',
                    key: 'discovery._game_stats.primary.id',
                    header: {
                        label: 'Primary game',
                        is_sortable: false,
                        tooltip: {
                            title: 'Primary game',
                            content: [
                                {
                                    text: 'The game the creator has covered the most within the selected timeframe.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field, item }) => {
                            if (field) {
                                return _.get(item, `superview.${contentType}.day_${metricWindow}.accumulation.games.lookup.${field}.name`)
                            }

                            return ''
                        },
                    },
                    is_exportable: true,
                    group: 'games',
                })

                fields.push({
                    id: 'game_secondary',
                    key: 'discovery._game_stats.secondary.id',
                    header: {
                        label: 'Secondary game',
                        is_sortable: false,
                        tooltip: {
                            title: 'Secondary game',
                            content: [
                                {
                                    text: 'The game the creator has covered the second most within the selected timeframe.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field, item }) => {
                            if (field) {
                                return _.get(item, `superview.${contentType}.day_${metricWindow}.accumulation.games.lookup.${field}.name`)
                            }

                            return ''
                        },
                    },
                    is_exportable: true,
                    group: 'games',
                })

                fields.push({
                    id: 'game_tertiary',
                    key: 'discovery._game_stats.tertiary.id',
                    header: {
                        label: 'Tertiary game',
                        is_sortable: false,
                        tooltip: {
                            title: 'Tertiary game',
                            content: [
                                {
                                    text: 'The game the creator has covered the third most within the selected timeframe.',
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        formatter: ({ field, item }) => {
                            if (field) {
                                return _.get(item, `superview.${contentType}.day_${metricWindow}.accumulation.games.lookup.${field}.name`)
                            }

                            return ''
                        },
                    },
                    is_exportable: true,
                    group: 'games',
                })
            }

            /*
             * TODO: Reinstate when audience coverage has improved.
            fields.push({
                id: 'audience_countries_primary',
                key: 'discovery.audience_countries[0].id',
                header: {
                    label: 'Primary Country',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Country',
                        content: [
                            {
                                text: 'The most common country of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_languages_primary',
                key: 'discovery.audience_languages[0].id',
                header: {
                    label: 'Primary Language',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Language',
                        content: [
                            {
                                text: 'The most common language of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_gender_distribution_primary',
                key: 'discovery.audience_gender_distribution[0].id',
                header: {
                    label: 'Primary Gender',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Gender',
                        content: [
                            {
                                text: 'The most common gender of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_age_ranges_primary',
                key: 'discovery.audience_age_ranges[0].id',
                header: {
                    label: 'Primary Age Range',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Age Range',
                        content: [
                            {
                                text: 'The most common age range of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_ethnicity_distribution_primary',
                key: 'discovery.audience_ethnicity_distribution[0].id',
                header: {
                    label: 'Primary Ethnicity',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Ethnicity',
                        content: [
                            {
                                text: 'The most common ethnicity of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_education_level_primary',
                key: 'discovery.audience_education_level[0].id',
                header: {
                    label: 'Primary Education Level',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Education Level',
                        content: [
                            {
                                text: 'The most common education level of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_family_status_primary',
                key: 'discovery.audience_family_status[0].id',
                header: {
                    label: 'Primary Family Status',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Family Status',
                        content: [
                            {
                                text: 'The most common family status of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_income_distribution_primary',
                key: 'discovery.audience_income_distribution[0].id',
                header: {
                    label: 'Primary Income Range',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Income Range',
                        content: [
                            {
                                text: 'The most common income range of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_occupation_distribution_primary',
                key: 'discovery.audience_occupation_distribution[0].id',
                header: {
                    label: 'Primary Occupation',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Occupation',
                        content: [
                            {
                                text: 'The most common occupation of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })

            fields.push({
                id: 'audience_engagement_style_primary',
                key: 'discovery.audience_engagement_style[0].id',
                header: {
                    label: 'Primary Engagement Style',
                    sub_label: 'Audience',
                    is_sortable: true,
                    tooltip: {
                        title: 'Primary Audience Engagement Style',
                        content: [
                            {
                                text: 'The most common engagement style of the creator\'s audience.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ field }) => field,
                },
                is_exportable: true,
                group: 'audience',
            })
            */


            // fields.push({
            //     id: 'alternate_platform',
            //     key: 'discovery.gender',
            //     header: {
            //         label: 'Gender',
            //         is_sortable: false,
            //         tooltip: {
            //             title: 'Gender',
            //             content: [
            //                 {
            //                     text: `This is an estimate made by AI to determine the gender of the creator.`,
            //                 },
            //             ],
            //         },
            //     },
            //     // row: {
            //     //     slot: 'code',
            //     //     code_type: 'gender',
            //     // },
            //     row: {
            //         class: '',
            //         formatter: ({ field }) => field,
            //     },
            //     is_exportable: true,
            //     group: 'basic',
            // })


            const gamesList = _.get(this.filtering.current, 'games_list.include.values')

            _.forEach(gamesList, (gameItem) => {
                const gameMetrics = [
                    {
                        'key': 'avg_views',
                        'label': 'Avg. views',
                        'tooltipTitle': 'Average views',
                        'tooltipText': 'Average views on content related to this game.',
                        format : {
                            isNumber: true,
                            decimals : 0,
                        },
                        platforms : ['youtube']
                    },
                    {
                        'key': 'content_count',
                        'label': 'Posts about this game',
                        'tooltipTitle': 'Posts about this game',
                        'tooltipText': 'Total amount of content the creator has made about this game.',
                        format : {
                            isNumber: true,
                            decimals : 0,
                        }
                    },
                    {
                        'key': 'count_percent',
                        'label': 'Content share',
                        'tooltipTitle': 'Content share',
                        'tooltipText': 'Percentage of the creator’s content related to this game within the selected timeframe.',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        }
                    },
                    {
                        'key': 'last_content_at',
                        'label': 'Last posted',
                        'tooltipTitle': 'Last posted',
                        'tooltipText': 'The most recent date the creator posted content about this game.',
                        format : {
                            isDate : true,
                            isTime : true,
                        }
                    },
                    {
                        'key': 'sponsored_count',
                        'label': 'Sponsored posts',
                        'tooltipTitle': 'Sponsored content share',
                        'tooltipText': 'Percentage of the creator’s content about this game that was sponsored.',
                        format : {
                            isNumber: true,
                            decimals : 0,
                        }

                    },
                    {
                        'key': 'sponsored_percent',
                        'label': 'Sponsored Content Percentage',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        }
                    },
                    {
                        'key': 'sum_views',
                        'label': 'Total views',
                        'tooltipTitle': 'Total views',
                        'tooltipText': 'Total views on content related to this game.',
                        format : {
                            isNumber: true,
                            decimals : 0,
                        },
                        platforms : ['youtube']
                    },
                    {
                        'key': 'sum_views_percent',
                        'label': 'View share',
                        'tooltipTitle': 'View share',
                        'tooltipText': 'Percentage of the creator’s total views generated by content about this game.',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        platforms : ['youtube']
                    },
                    {
                        'key': 'avg_viewers',
                        'label': 'AVG Viewers',
                        format : {
                            isNumber: true,
                            decimals : 0,
                        },
                        platforms : ['twitch']
                    },
                    {
                        'key': 'time_watched_percent',
                        'label': 'Percent of creator\'s watch time',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        platforms : ['twitch']
                    },
                    {
                        'key': 'time_streamed_percent',
                        'label': 'Percent of creator\'s stream time',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        platforms : ['twitch']
                    },
                    {
                        'key': 'time_streamed_minutes',
                        'label': 'Hours streamed',
                        format : {
                            isMinutes: true,
                        },
                        platforms : ['twitch']
                    }
                ]

                _.forEach(gameMetrics, ({ key, label, tooltipTitle, tooltipText, format, platforms }) => {

                    if (!platforms || platforms.includes(this.platformId)) {
                        fields.push({
                            id: `_game_stats.games.${gameItem.id}.${key}`,
                            key: `discovery._game_stats.games.${gameItem.id}.${key}`,
                            header: {
                                label,
                                sub_label: gameItem.label,
                                sub_group_label: gameItem.label,
                                is_sortable: false,
                                tooltip: {
                                    title: tooltipTitle || label,
                                    content: [
                                        {
                                            text: tooltipText || 'The game the creator has played the 3rd most during the aggregation window.',
                                        },
                                    ],
                                },
                            },
                            row: {
                                class: '',
                                formatter: ({field}) => {
                                    if (field) {
                                        if (format?.isPercentage) {
                                            return this.$filters.formatWholeNumber(field * 100)
                                        }
                                        if (format?.isDate && format?.isTime) {
                                            return this.$filters.formatDateAndTime(field)
                                        }
                                        if (format?.isNumber) {
                                            return this.$filters.formatWholeNumberWithUnit(field)
                                        }
                                        if (format?.isMinutes) {
                                            return this.$filters.formatWholeNumber(field / 60)
                                        }
                                    }
                                    return field
                                },
                            },
                            is_exportable: true,
                            group: 'games',
                        })
                    }
                })
            })

            return fields
        },

        setIsOpen(value) {
            this.isOpen = value
        },

        onSavedSearchCreated() {
            this.$refs.discoveryHistory?.refreshSavedSearches?.()
        },

        invalidateCreatorCount() {
            this.futureCount = null
            this.state.counts.isLoading = true
        },

        onReset() {
            this.filtering.current = _.cloneDeep(this.filtering.default)
            this.resetSorting()

            // Set the content type
            const firstContentType = _.first(this.filtering.options.content_type)

            if (firstContentType?.id) {
                this.filtering.current.content_type = firstContentType.id
            }

            this.paging.currentPage = 1
            this.invalidateCreatorCount()
            this.getCreatorCounts()
        },

        /**
         * We have isInitial because there are a number of things we don't want to do if this
         * is the initial page load
         */
        onPlatformChange(newPlatform) {
            setTimeout(() => {
                this.state.creators.showPreGeneration = true
                this.source.type = 'platform'
                this.source.input = null
                this.source.name = null

                // If we have swapped to a new platform, we need to reset all the filters
                if (newPlatform.type === 'platform') {
                    this.platformId = newPlatform.value.id
                    this.state.isFiltersDirty = true

                    if (this.filteringByPlatform[this.platformId]) {
                        this.filtering = this.filteringByPlatform[this.platformId]

                        if (!this.filtering.current) {
                            this.filtering.current = _.cloneDeep(this.filtering.default)
                        }

                        const firstContentType = _.first(this.filtering.options.content_type)

                        if (firstContentType?.id) {
                            this.filtering.current.content_type = firstContentType.id
                        }

                        this.resetSorting()
                    } else {
                        console.error('Error, platform set has no filtering configuration')
                    }
                }

                // Reset paging settings
                this.clear()

                this.setTargetGroups()

                this.invalidateCreatorCount()
                this.getCreatorCounts()

                // Auto-load base results for the new platform
                this.$nextTick(() => {
                    this.findCreators({
                        isExport: false,
                        isInitial: true,
                    })
                })
            }, 1) //Actions within this function are slow and block UI changes, delay slightly to allow UI actions to complete first (i.e. drop down closing)
        },

        onSourceChange(newSource) {
            if (!newSource) {
                this.source.type = 'platform'
                this.source.input = null
                this.source.name = null

                this.state.isFiltersDirty = true
                this.invalidateCreatorCount()
                this.getCreatorCounts()
                return
            }

            this.source.type = newSource.type
            this.source.input = newSource.input ?? newSource.value?.id
            this.source.name = newSource.name

            // If we have swapped to a new platform, we need to reset all the filters
            if (newSource.type === 'platform') {
                // this.platformId = newSource.value.id
                this.filtering = this.filteringByPlatform[this.platformId]

                this.filtering.current = _.cloneDeep(this.filtering.default)
            }

            this.state.isFiltersDirty = true
            this.invalidateCreatorCount()
            this.getCreatorCounts()

            // this.findCreators({
            //     isExport: false,
            //     isInitial: true,
            // })
        },

        getExcludedListSource() {
            const excludedListIds = _.get(this.filtering, 'current.excluded_list_id', [])

            return _.size(excludedListIds)
                ? {
                    type: 'list',
                    input: excludedListIds,
                }
                : null
        },

        onExcludedListChange(newSource) {
            const listIds = newSource?.input
                ? _.castArray(newSource.input)
                : _.castArray(newSource?.value?.id).filter(Boolean)

            this.onSingleFilterChange({
                key: 'excluded_list_id',
                value: listIds,
            })
        },

        clear() {
            this.creators = []
            this.paging.totalRecords = 0
            this.paging.totalPages = 1
            this.paging.currentPage = 1
        },

        checkSortingOverride() {
            const newQueryHasSetGame = _.size(this.filtering?.current?.games?.values) >= 1
            // const userHasManuallySorted = _.some(this.paging.sort, (item) => item.source !== 'default')

            // If games have been provided to search, and user has not manually sorted the table, let's
            // make sure to sort by affinity descending + avg viewers descending
            if (newQueryHasSetGame) {
                // if (!userHasManuallySorted) {
                // this.resetSorting()

                // const existingAffinitySort = _.find(this.paging.sort, (item) => item.field === 'affinity')

                // const hasExistingManualFilter = _.some(this.paging.sort, (item) => !['default', 'auto'].includes(item.source))

                // console.log(hasExistingManualFilter && !existingAffinitySort, !hasExistingManualFilter, !existingAffinitySort)

                // if (!hasExistingManualFilter && !existingAffinitySort) {
                //     this.paging.sort.unshift({
                //         field: 'affinity',
                //         direction: 'desc',
                //         source: 'auto',
                //     })
                // }
                // }

                // Make sure the affinity column shows
                if (!this.state.columns.enabled.includes('affinity')) {
                    this.state.columns.enabled.push('affinity')
                }
            } else {
                this.paging.sort = _.filter(this.paging.sort, (item) => item.field !== 'affinity')
                this.state.columns.enabled = _.filter(this.state.columns.enabled, (item) => item !== 'affinity')
            }
        },

        onPagingChange(updatedPaging) {
            _.assign(this.paging, updatedPaging)

            if (updatedPaging.perPage) {
                this.$store.commit('preferences:set', {
                    key: 'tables.discovery.rowsPerPage',
                    value: this.paging.perPage,
                })

                this.findCreators({
                    isExport: false,
                    isInitial: false,
                })
            }
            else {
                this.findCreators({
                    isExport: false,
                    isInitial: false,
                })
            }
        },

        onFilterChange() {
            this.checkSortingOverride()

            this.findCreators({
                isExport: false,
                isInitial: false,
            })
        },

        onSingleFilterChange(field) {

            if (_.get(this.filtering.current, field.key) !== field.value) {
                _.set(this.filtering.current, field.key, field.value)
            }

            this.checkSortingOverride()
            this.state.isFiltersDirty = true
            this.invalidateCreatorCount()
            this.getCreatorCounts()
        },

        async onApplyFilters() {
            if (this.state.isFiltersDirty === false) return

            // Trigger discovery search event
            trackEvent(AnalyticalEvents.DiscoverySearchCompleted, {
                creator_count: this.futureCount,
            })

            await this.findCreators({
                isExport: false,
                isInitial: false,
            })

            // Scroll to discover table on small screens
            // Helps indicate to the user that the table has been updated
            if (this.isSmallScreen) {
                this.$nextTick(() => {
                    const el = this.$refs.discoverTable
                    if (el && el.scrollIntoView) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                })
            }

            this.state.isFiltersDirty = false
        },

        resetSorting() {
            const currentContentType = this.filtering?.current?.content_type

            // console.log(`resetSorting`, currentContentType)

            if (currentContentType === 'stream') {
                this.paging.sort = [
                    {
                        field: 'avg_viewers',
                        direction: 'desc',
                        source: 'default',
                    },
                ]
            } else {
                // Default to views for all non-stream content types (video, post, image, carousel, short, etc.)
                this.paging.sort = [
                    {
                        field: 'avg_views',
                        direction: 'desc',
                        source: 'default',
                    },
                ]
            }
        },

        getCreatorCounts: _.debounce(
            async function () {
                // const filters = _.reduce(this.filtering.current, (results, value, key) => {
                //     // console.log(JSON.stringify(this.filtering.default[key]), JSON.stringify(value))
                //     if (JSON.stringify(this.filtering.default[key]) !== JSON.stringify(value)) {

                //         if (value?.include?.values?.length > 0) {
                //             results[key] = _.map(value?.include?.values, (includedValue) => {
                //                 const newIncludedValue = _.cloneDeep(includedValue)

                //                 if (includedValue?.sub_model) {
                //                     newIncludedValue.sub_model = _.reduce(includedValue.sub_model, (subModelResults, subModelValue, subModelKey) => {
                //                         const newSubModelValue = _.pickBy(subModelValue, item => item !== null)

                //                         if (_.size(newSubModelValue) > 0) {
                //                             subModelResults[subModelKey] = newSubModelValue
                //                         }

                //                         return subModelResults
                //                     }, {})
                //                 }

                //                 return newIncludedValue
                //             })
                //         } else {
                //             results[key] = value
                //         }
                //     }

                //     return results
                // }, {})

                const filters = _.reduce(this.filtering.current, (results, value, key) => {
                    if (JSON.stringify(this.filtering.default[key]) !== JSON.stringify(value)) {
                        results[key] = value
                    }

                    return results
                }, {})

                this.appendSourceFilter(filters)
                this.state.counts.isLoading = true

                // Clear all pending requests to API of this type
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/twitch/count')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/youtube/count')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/tiktok/count')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/twitter/count')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/instagram/count')

                let countCreatorsResponse

                try {
                    // Make sure to provide the cancel token to the request
                    countCreatorsResponse = await this.$axios.post(`/discovery/${this.platformId}/count`, {
                        collection: {
                            content_type: this.filtering.current.content_type,
                            day_range: String(this.filtering.current.day_range),
                        },
                        query: _.omit(filters, ['content_type', 'day_range']),
                        pagination: {
                            limit: this.paging.perPage,
                            skip: Math.max((this.paging.currentPage - 1) * this.paging.perPage, 0),
                            sort: this.paging.sort,
                        },
                        countLimit : this.countLimit,
                    })
                } catch (error) {
                    if (error?.is_cancelled === true) {
                        return
                    }
                    console.error('Error with countCreatorsResponse', error)
                }

                if (countCreatorsResponse?.data?.payload) {
                    this.futureCount = countCreatorsResponse?.data?.payload.count
                } else {
                    console.error('Error getting discovery counts', countCreatorsResponse)
                }

                this.state.counts.isLoading = false
            },
            1000,
            {
                leading: false,
            },
        ),

        findCreators: _.debounce(
            async function ({ isExport = false, isInitial = true }) {

                const filters = _.reduce(this.filtering.current, (results, value, key) => {
                    if (JSON.stringify(this.filtering.default[key]) !== JSON.stringify(value)) {
                        results[key] = value
                    }

                    return results
                }, {})

                const { paging } = this

                this.appendSourceFilter(filters)

                if (isInitial === true) {
                    this.state.creators.isLoading = true
                } else {
                    this.state.creators.isBusy = true
                }

                if (this.state.creators.showPreGeneration) {
                    this.state.creators.isLoading = true
                    this.state.creators.showPreGeneration = false
                }

                // Clear all pending requests to API of this type
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/twitch')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/youtube')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/tiktok')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/twitter')
                this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', '/discovery/instagram')

                // If we are exporting results w/ out a game filter, set page to 1
                if (isExport === true && _.size(filters?.games?.values) === 0) {
                    paging.currentPage = 1
                }

                let endpoint = ''
                let body = {}

                const validPlatformIds = ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']

                if (!validPlatformIds.includes(this.platformId)) {
                    throw new Error('Invalid platform ID provided to discovery API request')
                }
                endpoint = `/discovery/${this.platformId}`

                body = {
                    collection: {
                        content_type: this.filtering.current.content_type,
                        day_range: String(this.filtering.current.day_range),
                    },
                    query: _.omit(filters, ['content_type', 'day_range']),
                    pagination: {
                        limit: paging.perPage,
                        skip: Math.max((paging.currentPage - 1) * paging.perPage, 0),
                        sort: paging.sort,
                    },
                    countLimit : this.countLimit,
                }

                if (isExport === true) {
                    body.export = true
                    body.paging.perPage = 500
                }

                return this.$axios
                    .post(endpoint, body)
                    .then(({ data }) => {
                        this.state.creators.isLoading = false
                        this.state.creators.isBusy = false

                        this.paging.totalRecords = data.payload.count
                        this.paging.totalPages = Math.ceil(data.payload.count / this.paging.perPage)

                        _.forEach(data.payload.creators, creator => {

                            creator.discovery._game_stats = {
                                games : {},
                                primary : null,
                                secondary : null,
                                tertiary : null,
                            }

                            if (creator.discovery.games_list && creator.discovery.games_list.length > 0) {
                                const sortedCreatorGames = _.sortBy(creator.discovery.games_list, creatorGame => { return creatorGame.content_count}).reverse()

                                _.forEach(sortedCreatorGames, creatorGame => {
                                    creator.discovery._game_stats.games[creatorGame.id] = creatorGame
                                })

                                creator.discovery._game_stats.primary = sortedCreatorGames.length > 0 ? sortedCreatorGames[0] : null
                                creator.discovery._game_stats.secondary = sortedCreatorGames.length > 1 ? sortedCreatorGames[1] : null
                                creator.discovery._game_stats.tertiary = sortedCreatorGames.length > 2 ? sortedCreatorGames[2] : null
                            }
                        })

                        this.creators = data.payload.creators

                        this.$router.replace({
                            query: {
                                ...this.$route.query,
                                id: data.payload.requestHash
                            },
                        })
                    })
                    .catch((error) => {
                        console.error('HTTP Error to discovery endpoint', error)
                    })

            },
            1000,
            {
                leading: true,
            },
        ),

        appendSourceFilter(filters) {
            if (this.source.type === 'platform' || !this.source.input || _.size(this.source.input) === 0) {
                return
            }

            if (this.source.type === 'mixed') {
                const listIds = _.castArray(this.source.input.list_id).filter(Boolean)
                const campaignIds = _.castArray(this.source.input.campaign_id).filter(Boolean)

                if (listIds.length) {
                    // eslint-disable-next-line no-param-reassign
                    filters.list_id = listIds
                }

                if (campaignIds.length) {
                    // eslint-disable-next-line no-param-reassign
                    filters.campaign_id = campaignIds
                }

                return
            }

            const sourceTypes = [
                { name: 'list', field: 'list_id' },
                { name: 'campaign', field: 'campaign_id' },
            ]

            const sourceField = _.find(sourceTypes, (item) => item.name === this.source.type)

            if (sourceField) {
                // eslint-disable-next-line no-param-reassign
                filters[sourceField.field] = this.source.input
            }
        },

        getLists() {
            return this.$axios
                .get('/lists')
                .then(({ data }) => {
                    this.lists = _.map(data.payload.myLists, (list) => {
                        // eslint-disable-next-line no-param-reassign
                        list.type = 'user'
                        return list
                    })

                    const allLists = [...data.payload.myLists, ...data.payload.sharedLists]

                    // We have to do some intense platform splitting because lists can contain creators from multiple platforms
                    // and our search algorithm can & should only work on one platform at a time

                    this.listsByPlatformId = this.filterItemsIntoPlatforms(allLists)
                })
                .catch((error) => {
                    console.log(error)
                })
        },

        filterItemsIntoPlatforms(items) {
            const platformsToGroupBy = ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']
            const groupedByPlatform = {}

            _.forEach(platformsToGroupBy, (platformId) => {
                groupedByPlatform[platformId] = []
            })

            _.forEach(items, (item) => {
                _.forEach(platformsToGroupBy, (platformId) => {
                    if (_.get(item, `totals.${platformId}`, 0) > 0) {
                        groupedByPlatform[platformId].push(item)
                    }
                })
            })

            return groupedByPlatform
        },

        getSupercampaigns() {
            this.$axios
                .get('/campaigns/summary', {
                    params: {},
                })
                .then(({ data }) => {
                    this.campaigns = _(data.payload).orderBy(['created_at'], ['desc']).value()
                    this.campaignsByPlatformId = this.filterItemsIntoPlatforms(this.campaigns)
                })
                .catch((error) => {
                    this.$errorHandler(error, {
                        title: 'Supercampaigns',
                        message: 'Error fetching supercampaigns.',
                    })
                })
        },

        addListCreators({ creators, list }) {
            this.$axios
                .post(`/lists/${list.id}/creators`, {
                    creators,
                })
                .then(({ data }) => {
                    // update list creator count on sidebar
                    const listToUpdate = _.find(this.lists, { id: list.id })
                    listToUpdate.creators.push(data.payload.creators)
                })
                .catch((error) => {
                    console.log(error)
                })
        },

        addCreatorsToCampaign({ creators, supercampaignId, campaignId, type }) {
            if (type === 'add-selected') {
                // console.log('add selected creators')
                const body = {
                    creators,
                }

                this.$axios
                    .post(`/supercampaigns/${supercampaignId}/campaigns/${campaignId}/participants`, body)
                    .then(() => {
                        // console.log('Added selected creators')
                    })
                    .catch((error) => {
                        console.log(error)
                    })
            }

            if (type === 'add-all') {
                const body = {
                    creators,
                }

                this.$axios
                    .post(`/supercampaigns/${supercampaignId}/campaigns/${campaignId}/participants`, body)
                    .then(() => {
                        // console.log('Added selected creators')
                    })
                    .catch((error) => {
                        console.log(error)
                    })
            }
        },

        setTargetGroups() {
            let groups = _.filter(
                this.targetGroups.groups,
                (targetingGroup) => targetingGroup.platform === this.platformId,
            )

            groups = _.sortBy(groups, (targetingGroup) => targetingGroup.name)
            this.platformGroups = groups
        },

        async getTargetGroups() {
            const targetingGroupsResponse = await targetGroupAPI.getAvailableTargetGroups(this.$axios)
            if (targetingGroupsResponse.success) {
                this.targetGroups = targetingGroupsResponse.value
                this.setTargetGroups()
            }
        },

        loadTargetGroup(group, codes) {
            let codesToUse = codes

            if (
                _.find(this.targetGroups?.groups, (existingGroup) => {
                    return group.id === existingGroup.id
                })
            ) {
                codesToUse = this.targetGroups.codes
            }

            const loadedGroup = targetGroupTools.mapTargetGroupFromFilters(group, codesToUse, {
                twitch: this.filteringByPlatform.twitch.default,
                youtube: this.filteringByPlatform.youtube.default,
                tiktok: this.filteringByPlatform.tiktok.default,
                twitter: this.filteringByPlatform.twitter.default,
                instagram: this.filteringByPlatform.instagram.default,
            })

            this.setGroup(group.platform, loadedGroup)
        },

        setGroup(platformId, group) {
            // eslint-disable-next-line no-multi-assign
            this.filtering.current = this.filteringByPlatform[platformId].current = group
            this.checkSortingOverride()
        },

        getDemographicCode(demographicType, id) {
            const code = _.find(this.metadata.demographics[demographicType], (item) => item.id === id)

            if (code) {
                return code.platforms.web.code
            }

            return ''
        },

        appendDemographicColumns(fields, demographicType) {
            if (this.filtering.current.demographics) {
                if (this.filtering.current.demographics[demographicType]) {
                    _.forEach(this.filtering.current.demographics[demographicType].values, (demographic) => {
                        const keyCode = this.getDemographicCode(demographicType, demographic.id)
                        fields.push({
                            id: `demographics.${demographicType}.${keyCode}`,
                            key: `demographics.${demographicType}.${keyCode}`,
                            header: {
                                label: demographic.label,
                                is_sortable: true,
                            },
                            row: {
                                class: '',
                                formatter: ({ field }) => {
                                    if (field) {
                                        return `${this.$filters.formatWholeNumber(field)}%`
                                    }
                                    return ''
                                },
                            },
                            is_exportable: true,
                        })
                    })
                }
            }
        },

        onEnabledColumnChange(newEnabledColumns) {
            this.state.columns.enabled = this.filterEnabledColumns(newEnabledColumns)

            this.$store.commit('preferences:set', {
                key: `tables.discovery.${this.platformId}.columns`,
                value: this.state.columns.enabled,
            })
        },


        onExportCreators() {
            this.$notify({
                type: 'info',
                text: 'Exporting list of creators... Please wait a moment.',
                duration: 10000,
                speed: 1000,
            })
            this.findCreators({
                isExport: true,
                isInitial: false,
            })
        },

        async export(data) {
            try {
                const { data: creatorData } = await this.$axios.post(`/creators/${this.platformId}`, {
                    platform_user_ids: data.creators.map((creator) => creator._id),
                })

                const link = document.createElement('a')

                const mappedFields = _.map(data.creators, (creator) => {
                    const result = {}

                    _.forEach(this.tableColumns, (column) => {
                        if (!column.is_exportable) return

                        if (_.has(column, 'exportTransformer')) {
                            _.assign(result, column.exportTransformer(creator))
                        } else {
                            const value = _.get(creator, column.key)
                            result[column.header.label] = value !== undefined && value !== null ? value : ''
                        }
                    })

                    // Overriding with values from creator linking
                    const linkedCreator = creatorData.find((item) =>
                        item.platforms.find(
                            (platform) =>
                                platform.platform_user_id === creator._id && platform.platform_id === this.platformId,
                        ),
                    )

                    if (this.isSfStaff) {
                        if (_.has(linkedCreator, 'metadata.primaryEmail')) {
                            result.Email = _.get(linkedCreator, 'metadata.primaryEmail')
                        } else if (_.has(creator.superview, 'normalized.email')) {
                            result.Email = _.get(creator.superview, 'normalized.email')
                        } else {
                            result.Email = ''
                        }
                    }

                    return result
                })

                const csvResults = await json2csvAsync(mappedFields)

                link.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(csvResults)}`)
                link.setAttribute('download', 'creators.csv')
                link.style.visibility = 'hidden'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            } catch (error) {
                console.error('Error exporting creators', error)
                this.$notify.error({
                    title: 'Error exporting creators',
                    text: 'There was an error exporting the list of creators. Please try again.',
                })
            }
        },

        onCreatorProfileModalClose() {
            // This method is no longer needed as CreatorModal handles closing
            // Keeping for backwards compatibility but redirecting to modal:close
            this.$store.commit('modal:close', 'creator')
        },

        onResize() {
            this.isSmallScreen = window.innerWidth < BREAKPOINTS.md
        },

         checkScrollbar() {
            const el = this.$refs.scrollContainer
            if (el) {
                this.hasScrollbar = el.scrollHeight > el.clientHeight
            }
        },
    },
}
</script>
